import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    revokeMedicalRecordExport: build.mutation<
      RevokeMedicalRecordExportApiResponse,
      RevokeMedicalRecordExportApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/medical-record-exports/${queryArg.exportId}`,
        method: "DELETE",
      }),
    }),
    getMedicalRecordExportStatus: build.query<
      GetMedicalRecordExportStatusApiResponse,
      GetMedicalRecordExportStatusApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/medical-record-exports/${queryArg.exportId}`,
      }),
    }),
    downloadMedicalRecordExport: build.query<
      DownloadMedicalRecordExportApiResponse,
      DownloadMedicalRecordExportApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/medical-record-exports/${queryArg.exportId}/download`,
      }),
    }),
    getMedicalRecordExportOptions: build.query<
      GetMedicalRecordExportOptionsApiResponse,
      GetMedicalRecordExportOptionsApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/medical-record-export-options`,
      }),
    }),
    startMedicalRecordExport: build.mutation<
      StartMedicalRecordExportApiResponse,
      StartMedicalRecordExportApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/medical-record-exports`,
        method: "POST",
        body: queryArg.startMedicalRecordExportRequest,
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type RevokeMedicalRecordExportApiResponse =
  /** status 200 OK */ MedicalRecordExportStatusResponse;
export type RevokeMedicalRecordExportApiArg = {
  exportId: string;
};
export type GetMedicalRecordExportStatusApiResponse =
  /** status 200 OK */ MedicalRecordExportStatusResponse;
export type GetMedicalRecordExportStatusApiArg = {
  exportId: string;
};
export type DownloadMedicalRecordExportApiResponse =
  /** status 200 OK */ MedicalRecordExportDownloadResponse;
export type DownloadMedicalRecordExportApiArg = {
  exportId: string;
};
export type GetMedicalRecordExportOptionsApiResponse =
  /** status 200 OK */ MedicalRecordExportOptionsResponse;
export type GetMedicalRecordExportOptionsApiArg = {
  patientId: string;
};
export type StartMedicalRecordExportApiResponse =
  /** status 202 Accepted */ StartMedicalRecordExportResponse;
export type StartMedicalRecordExportApiArg = {
  patientId: string;
  startMedicalRecordExportRequest: StartMedicalRecordExportRequest;
};
export type MedicalRecordFailureCode =
  | "selection_stale"
  | "record_not_accessible"
  | "record_not_found"
  | "note_not_eligible"
  | "unsupported_document_type"
  | "invalid_document_page_range"
  | "source_unreadable"
  | "signed_copy_unavailable"
  | "page_limit_exceeded"
  | "byte_limit_exceeded"
  | "export_expired"
  | "export_revoked"
  | "internal_error";
export type MedicalRecordOutputMode = "combined_pdf" | "separate_pdfs_zip";
export type MedicalRecordWarningCode = "signed_pdf_missing_rendered_current";
export type MedicalRecordExportStatusResponse = {
  artifact_expires_at?: string | null;
  completed_at?: string | null;
  created_at?: string | null;
  document_count?: number;
  encounter_count?: number;
  export_id: string;
  /** ID of the selected record the failure is attributed to, when known — lets the client name the affected selection */
  failed_record_id?: string | null;
  /** encounter | note | document */
  failed_record_kind?: string | null;
  failure_code?: MedicalRecordFailureCode | null;
  failure_stage?: string | null;
  file_count?: number;
  /** Patient-friendly output filename (computed per request; never stored in job metadata) */
  filename?: string | null;
  generated_note_count?: number;
  note_count?: number;
  /** True when the packet is completed, unexpired, and not revoked — i.e. a download link can be issued right now */
  output_available: boolean;
  output_deleted_at?: string | null;
  output_mode: MedicalRecordOutputMode;
  output_size_bytes?: number | null;
  page_count?: number;
  patient_id?: string | null;
  signed_note_count?: number;
  status: string;
  warnings?: MedicalRecordWarningCode[];
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type MedicalRecordExportDownloadResponse = {
  download_url: string;
  expires_in: number;
  filename: string;
};
export type MedicalRecordDocumentPageRangeSchema = {
  end: number;
  start: number;
  /** Total pages of the source Artifact when known */
  total?: number | null;
};
export type MedicalRecordDocumentOptionSchema = {
  authored_at?: string | null;
  document_reference_id: string;
  /** doc_kind label when known */
  document_type?: string | null;
  eligible?: boolean;
  /** 'missing_source_artifact' or 'unsupported_document_type' when the record cannot be exported in MVP */
  ineligible_reason?: string | null;
  page_estimate: number;
  source_page_range?: MedicalRecordDocumentPageRangeSchema | null;
  title: string;
};
export type MedicalRecordNotePdfSource = "signed" | "generated";
export type MedicalRecordNoteOptionSchema = {
  /** Note template/type label when known */
  document_type?: string | null;
  note_id: string;
  /** Heuristic page estimate; actual counts are enforced by the job */
  page_estimate: number;
  /** 'signed' (immutable compliance PDF) or 'generated' (current note render) */
  pdf_source: MedicalRecordNotePdfSource;
  /** ISO timestamp of the author signature when signed */
  signed_at?: string | null;
  title: string;
};
export type MedicalRecordEncounterOptionSchema = {
  encounter_id: string;
  notes?: MedicalRecordNoteOptionSchema[];
  /** ISO timestamp used for chronological ordering */
  occurred_at?: string | null;
  provider_name?: string | null;
  title: string;
};
export type MedicalRecordExportLimitsSchema = {
  max_bytes?: number;
  max_pages?: number;
};
export type MedicalRecordExportOptionsResponse = {
  documents?: MedicalRecordDocumentOptionSchema[];
  encounters?: MedicalRecordEncounterOptionSchema[];
  /** Notes hidden from the picker because generation is incomplete or completion state is not completed/signed (in-progress, reviewing, awaiting cosign, edits requested, or failed) */
  excluded_note_count?: number;
  limits?: MedicalRecordExportLimitsSchema;
  patient_id: string;
};
export type MedicalRecordOutputMode2 = "combined_pdf" | "separate_pdfs_zip";
export type StartMedicalRecordExportResponse = {
  /** True when an identical active request already existed and its job was returned instead of creating a duplicate */
  deduplicated?: boolean;
  export_id: string;
  output_mode: MedicalRecordOutputMode2;
  status: string;
};
export type MedicalRecordOrdering = "chronological";
export type MedicalRecordOutputMode3 = "combined_pdf" | "separate_pdfs_zip";
export type StartMedicalRecordExportRequest = {
  document_reference_ids?: string[];
  encounter_ids?: string[];
  /** The exact checked notes at submit time */
  note_ids?: string[];
  ordering?: MedicalRecordOrdering;
  output_mode?: MedicalRecordOutputMode3;
};
export const {
  useRevokeMedicalRecordExportMutation,
  useGetMedicalRecordExportStatusQuery,
  useDownloadMedicalRecordExportQuery,
  useGetMedicalRecordExportOptionsQuery,
  useStartMedicalRecordExportMutation,
} = injectedRtkApi;
